import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';
import FloatingContact from '../../../components/feature/FloatingContact';

export default function MinimalistMimariTasarimIlkeleri() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Minimalist Mimari Tasarım İlkeleri | Levent Atalay';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Minimalizm mimarlıkta sadece bir estetik tercih değil, mekânın özüne inme çabasıdır. Az ile çoğu ifade etmenin yollarını keşfedin.');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <FloatingContact />
      
      {/* Hero Image */}
      <div className="relative w-full h-96 md:h-[500px] mt-20">
        <img
          src="https://public.readdy.ai/ai/img_res/6884895a020850a5a68dced2db2b22cc.jpg"
          alt="Minimalist Mimari Tasarım"
          className="w-full h-full object-cover object-top"
        />
      </div>

      {/* Article Content */}
      <article className="max-w-3xl mx-auto px-6 py-16">
        {/* Meta */}
        <div className="flex items-center gap-4 text-sm text-gray-500 mb-8">
          <span className="text-gray-900 font-medium">Tasarım Felsefesi</span>
          <span>•</span>
          <span>15 Ocak 2025</span>
          <span>•</span>
          <span>8 dk okuma</span>
        </div>

        {/* Title */}
        <h1 className="font-serif text-4xl md:text-5xl text-gray-900 mb-8 leading-tight">
          Minimalist Mimari Tasarım İlkeleri
        </h1>

        {/* Content */}
        <div className="prose prose-lg max-w-none">
          <p className="text-gray-600 leading-relaxed mb-6">
            Minimalizm, mimarlıkta yalnızca bir estetik tercih olmanın ötesinde, mekânın özüne inme ve gereksiz her şeyi ayıklama çabasıdır. "Az çoktur" (Less is more) ilkesiyle özetlenen bu yaklaşım, modern mimarlığın temel taşlarından biri haline gelmiştir.
          </p>

          <h2 className="font-serif text-2xl md:text-3xl text-gray-900 mt-12 mb-6">
            Minimalizmin Temelleri
          </h2>

          <p className="text-gray-600 leading-relaxed mb-6">
            Minimalist mimari, sadeleştirilmiş formlar, temiz çizgiler ve işlevsel yaklaşımlarla karakterize edilir. Bu tasarım felsefesi, mekânda gereksiz detayları ortadan kaldırarak, asıl önemli olanı ön plana çıkarır. Her eleman bir amaca hizmet eder ve mekânın bütünlüğüne katkıda bulunur.
          </p>

          <p className="text-gray-600 leading-relaxed mb-6">
            Minimalist tasarımda renk paleti genellikle nötr tonlarla sınırlıdır. Beyaz, gri, bej ve doğal malzeme renkleri, mekâna sakinlik ve denge kazandırır. Bu renk seçimi, mekânın ferah ve geniş algılanmasını sağlarken, dikkat dağıtıcı unsurları minimize eder.
          </p>

          <h2 className="font-serif text-2xl md:text-3xl text-gray-900 mt-12 mb-6">
            Işık ve Mekân İlişkisi
          </h2>

          <p className="text-gray-600 leading-relaxed mb-6">
            Minimalist mimarlıkta doğal ışık, en önemli tasarım elemanlarından biridir. Geniş pencereler, cam yüzeyler ve açık planlar, doğal ışığın mekâna maksimum düzeyde girmesini sağlar. Işık, mekânın atmosferini belirler ve günün farklı saatlerinde değişen bir dinamizm yaratır.
          </p>

          <p className="text-gray-600 leading-relaxed mb-6">
            Yapay aydınlatma da aynı özenle tasarlanır. Gizli aydınlatmalar, lineer ışık kaynakları ve minimal armatürler, mekânın sadeliğini bozmadan gerekli aydınlatmayı sağlar. Işık, sadece fonksiyonel bir ihtiyaç değil, aynı zamanda mekânı şekillendiren bir araçtır.
          </p>

          <h2 className="font-serif text-2xl md:text-3xl text-gray-900 mt-12 mb-6">
            Malzeme Seçimi ve Doku
          </h2>

          <p className="text-gray-600 leading-relaxed mb-6">
            Minimalist tasarımda malzeme seçimi büyük önem taşır. Doğal malzemeler tercih edilir: ahşap, taş, beton, cam. Bu malzemeler, kendi doğal dokularıyla mekâna zenginlik katarken, sadeliği korur. Her malzemenin kendine özgü karakteri, mekânın kimliğini oluşturur.
          </p>

          <p className="text-gray-600 leading-relaxed mb-6">
            Malzemelerin ham halleriyle kullanılması, minimalist estetiğin önemli bir parçasıdır. Cilasız beton, doğal ahşap yüzeyler, işlenmemiş taş, mekâna otantiklik ve dürüstlük kazandırır. Bu yaklaşım, malzemenin gerçek doğasını ortaya çıkarır.
          </p>

          <h2 className="font-serif text-2xl md:text-3xl text-gray-900 mt-12 mb-6">
            Fonksiyon ve Estetik Dengesi
          </h2>

          <p className="text-gray-600 leading-relaxed mb-6">
            Minimalist mimarlıkta form, fonksiyonu takip eder. Her tasarım kararı, işlevsel bir ihtiyaca yanıt verir. Ancak bu, estetiğin ihmal edildiği anlamına gelmez. Aksine, fonksiyonellik ve estetik, birbirini tamamlayan unsurlar olarak ele alınır.
          </p>

          <p className="text-gray-600 leading-relaxed mb-6">
            Gizli dolaplar, entegre sistemler ve çok amaçlı mobilyalar, mekânın düzenli ve sade görünmesini sağlar. Her şeyin bir yeri vardır ve görsel karmaşa minimize edilir. Bu yaklaşım, hem estetik hem de pratik açıdan değer yaratır.
          </p>

          <h2 className="font-serif text-2xl md:text-3xl text-gray-900 mt-12 mb-6">
            Boşluk ve Denge
          </h2>

          <p className="text-gray-600 leading-relaxed mb-6">
            Minimalist tasarımda boşluk, dolu kadar önemlidir. Negatif alan, mekâna nefes aldırır ve önemli elementlerin öne çıkmasını sağlar. Dengeli bir kompozisyon, mekânda huzur ve düzen hissi yaratır.
          </p>

          <p className="text-gray-600 leading-relaxed mb-6">
            Açık plan düzenlemeler, mekânlar arası akışkanlığı artırır. Duvarlar yerine, seviye farkları, mobilya düzenleri veya malzeme değişimleriyle mekânlar tanımlanır. Bu yaklaşım, görsel süreklilik ve genişlik hissi yaratır.
          </p>

          <h2 className="font-serif text-2xl md:text-3xl text-gray-900 mt-12 mb-6">
            Detay ve Hassasiyet
          </h2>

          <p className="text-gray-600 leading-relaxed mb-6">
            Minimalist mimarlıkta detaylar kritik öneme sahiptir. Her birleşim noktası, her köşe, her geçiş özenle tasarlanır. Gizli profiller, derzsiz yüzeyler ve hassas işçilik, minimalist estetiğin vazgeçilmez unsurlarıdır.
          </p>

          <p className="text-gray-600 leading-relaxed mb-6">
            Bu hassasiyet, sadece görsel bir kaygı değil, aynı zamanda kalite ve dayanıklılık anlayışının bir yansımasıdır. İyi tasarlanmış detaylar, yapının uzun ömürlü olmasını ve zamanla değer kazanmasını sağlar.
          </p>

          <h2 className="font-serif text-2xl md:text-3xl text-gray-900 mt-12 mb-6">
            Sonuç
          </h2>

          <p className="text-gray-600 leading-relaxed mb-6">
            Minimalist mimari tasarım, basitlik gibi görünse de, aslında derin bir düşünce ve özen gerektirir. Her kararın bilinçli olarak alındığı, her elemanın bir amaca hizmet ettiği bu yaklaşım, zamansız ve değerli mekânlar yaratır.
          </p>

          <p className="text-gray-600 leading-relaxed mb-6">
            Az ile çoğu ifade etmek, gereksizi ayıklamak ve özü ortaya çıkarmak, minimalist mimarlığın temel ilkeleridir. Bu ilkeler, sadece estetik bir tercih değil, aynı zamanda yaşam felsefesinin mekâna yansımasıdır.
          </p>
        </div>

        {/* Back to Blog */}
        <div className="mt-16 pt-8 border-t border-gray-200">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-gray-900 hover:text-gray-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            <span>Tüm Yazılara Dön</span>
          </Link>
        </div>
      </article>

      <Footer />
    </div>
  );
}